/**
 * Contrato tipado de todas as feature flags disponíveis no app logged-home.
 */
export interface FeatureFlags {
  /**
   * Habilita a nova experiência OneView (alerta `new` + tooltip do
   * Patrimônio) para clientes tombados (Santander), independentemente de
   * serem Exclusivos ou Varejo.
   */
  oneviewExperienceSantanderEnabled: boolean;

  /**
   * Habilita a experiência antiga OneView (alerta `old`, sem tooltip) para
   * clientes não tombados, independentemente de serem Exclusivos ou Varejo.
   */
  oneviewExperienceGenericEnabled: boolean;
}

/**
 * Valores padrão das flags. Usados como fallback quando o Firebase Remote
 * Config não está disponível ou a chave ainda não foi carregada.
 */
export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  oneviewExperienceSantanderEnabled: false,
  oneviewExperienceGenericEnabled: false,
};

/**
 * Mapeia cada chave tipada para o nome base configurado no Firebase Remote
 * Config. O sufixo por ambiente (ex.: `_hml`) é aplicado em runtime — ver
 * `buildRemoteKey` / `getFeatureFlagRemoteKeys`.
 */
export const FEATURE_FLAG_REMOTE_BASE_KEYS: Record<keyof FeatureFlags, string> = {
  oneviewExperienceSantanderEnabled: 'oneview_experience_santander_enabled',
  oneviewExperienceGenericEnabled: 'oneview_experience_generic_enabled',
};

/**
 * Nome canônico do ambiente de produção, conforme definido em
 * `apps/logged-home/src/environment-settings/environment.prd.json` (`"env": "production"`).
 *
 * NUNCA renomear este valor sem alinhar simultaneamente o campo `env` do
 * `environment.prd.json`. Um mismatch faz produção cair em `_hml`
 * silenciosamente — cenário perigoso: prd sem experiência OneView.
 */
export const PRODUCTION_ENVIRONMENT_NAME = 'production' as const;

/**
 * Sufixo de chave remota por ambiente.
 *
 * Retorna string vazia apenas para o valor EXATO `PRODUCTION_ENVIRONMENT_NAME`.
 * Qualquer outro valor (`'homologation'`, `'development'`, `'localhost'`,
 * `'prd'`, `undefined`, `null`, string vazia) usa o grupo HML — comportamento
 * fail-safe: na dúvida, testamos em HML, nunca em produção.
 */
export function getRemoteConfigSuffix(environmentName: string | undefined | null): string {
  return environmentName === PRODUCTION_ENVIRONMENT_NAME ? '' : '_hml';
}

/**
 * Constrói a chave remota final para uma flag, aplicando o sufixo do ambiente.
 */
export function buildRemoteKey(
  flagKey: keyof FeatureFlags,
  environmentName: string | undefined | null,
): string {
  return `${FEATURE_FLAG_REMOTE_BASE_KEYS[flagKey]}${getRemoteConfigSuffix(environmentName)}`;
}

/**
 * Retorna o mapa completo de chaves remotas resolvidas para um ambiente.
 */
export function getFeatureFlagRemoteKeys(
  environmentName: string | undefined | null,
): Record<keyof FeatureFlags, string> {
  const suffix = getRemoteConfigSuffix(environmentName);
  return {
    oneviewExperienceSantanderEnabled: `${FEATURE_FLAG_REMOTE_BASE_KEYS.oneviewExperienceSantanderEnabled}${suffix}`,
    oneviewExperienceGenericEnabled: `${FEATURE_FLAG_REMOTE_BASE_KEYS.oneviewExperienceGenericEnabled}${suffix}`,
  };
}
